
export const generateLabReportHTML = (reportData, selectedPatient, userInfo, companyInfo, categoryInterpretations = {}, categoryHistograms = {}, categoryNarratives = {}) => {
  const dateOfResult = Object.keys(reportData)[0] || '';
  
  // Use company info or defaults
  const company = companyInfo && companyInfo.length > 0 ? companyInfo[0] : {
    name: 'JSB',
    location: 'ABRAFI ESTATE',
    digital_add: 'P.O.BOX',
    phone: '0352290063/0202369814',
    email: '-',
    mmda: 'Quality HealthCare Our Priority'
  };
  
  const labsHTML = Object.keys(reportData)
    .sort((a, b) => new Date(b) - new Date(a))
    .map((date) => {
      return Object.keys(reportData[date])
        .sort()
        .map((testName) => {
          const results = reportData[date][testName];
          
          // Separate CONCLUSION from other findings
          const nonConclusionResults = results.filter(r => !r.parameter || !r.parameter.toUpperCase().includes('CONCLUSION'));
          const conclusionResults = results.filter(r => r.parameter && r.parameter.toUpperCase().includes('CONCLUSION'));
          const sortedResults = [...nonConclusionResults, ...conclusionResults];
          
          const parametersHTML = sortedResults
            .map(result => {
              // When the parameter is dual-unit, stack both values inside the
              // single Result cell so the printed report stays the original
              // 5-column layout (Parameter / Result / Range / Flag / Unit).
              const hasAlt = result.result_alt && result.unit_alt;
              const resultCell = hasAlt
                ? `${result.result || '-'} <span style="color:#666;font-size:9pt;">(${result.result_alt} ${result.unit_alt})</span>`
                : (result.result || '-');
              const unitCell = hasAlt
                ? `${result.unit || '-'} / ${result.unit_alt}`
                : (result.unit || '-');
              return `
              <tr>
                <td>${result.parameter || '-'}</td>
                <td>${resultCell}</td>
                <td>${result.range || '-'}</td>
                <td>${result.flag || '-'}</td>
                <td>${unitCell}</td>
              </tr>
            `;
            })
            .join('');
          
          const interp = categoryInterpretations[testName] || '';
          const interpBlock = interp
            ? `<div class="interpretation-block">${interp}</div>`
            : '';

          const hists = Array.isArray(categoryHistograms[testName]) ? categoryHistograms[testName] : [];
          const histogramsBlock = hists.length > 0
            ? `<div class="histograms-block">
                 <div class="histograms-title">Distribution Graphs</div>
                 <div class="histograms-grid">
                   ${hists.map(h => `
                     <div class="histogram-card">
                       <div class="histogram-name">${(h.name || '').replace(/\.\s*(BMP|PNG|JPEG|GIF).*$/i, '').trim()}</div>
                       <img class="histogram-img" src="${h.dataUri}" alt="${h.name || 'Histogram'}" />
                     </div>
                   `).join('')}
                 </div>
               </div>`
            : '';

          // Analyzer-emitted narratives ("WBC Message", "RBC Message",
          // "PLT Message"). Grouped by message category and rendered
          // as side-by-side columns so a long WBC alert list doesn't
          // stretch the report vertically.
          const narrs = Array.isArray(categoryNarratives[testName]) ? categoryNarratives[testName] : [];
          const narrativesBlock = (() => {
            if (narrs.length === 0) return '';
            const groups = new Map();
            for (const n of narrs) {
              const key = (n.name || 'Message').toString().trim();
              const items = String(n.text || '')
                .split(/[,;~\r\n]+/)
                .map(s => s.trim())
                .filter(Boolean);
              if (items.length === 0) continue;
              if (!groups.has(key)) groups.set(key, []);
              groups.get(key).push(...items);
            }
            if (groups.size === 0) return '';
            const cols = Array.from(groups.entries())
              .map(([name, items]) => `
                <div class="narrative-col">
                  <div class="narrative-col-title">${name}</div>
                  <ul class="narrative-list">
                    ${items.map(it => `<li>${it}</li>`).join('')}
                  </ul>
                </div>
              `).join('');
            return `<div class="narratives-block">
                      <div class="narratives-title">Analyzer Notes</div>
                      <div class="narratives-grid">${cols}</div>
                    </div>`;
          })();

          return `
            <div class="lab-section">
              <div class="lab-title">${testName.toUpperCase()}</div>

              <table>
                <thead>
                  <tr>
                    <th>Test</th>
                    <th>Value</th>
                    <th>Range</th>
                    <th>Flag</th>
                    <th>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  ${parametersHTML}
                </tbody>
              </table>
              ${narrativesBlock}
              ${histogramsBlock}
              ${interpBlock}
            </div>
          `;
        })
        .join('');
    })
    .join('');

  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Patient Lab Results - ${selectedPatient?.fullName || 'Patient'}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html, body {
          width: 100%;
          font-family: 'Segoe UI', 'Trebuchet MS', 'Arial', sans-serif;
          font-size: 10pt;
          line-height: 1.5;
          color: #555;
        }

        body {
          padding: 0 !important;
          margin: 0 !important;
          background: #f5f5f5;
          min-height: 100vh;
        }

        .screen-only {
          display: block;
          padding: 20px;
          background-color: white;
        }

        .button-container {
          text-align: center;
          margin-bottom: 20px;
          padding: 15px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        button {
          padding: 10px 24px;
          margin: 0 8px;
          font-size: 11pt;
          cursor: pointer;
          background: #333;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          transition: all 0.3s ease;
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }

        button:hover {
          transform: translateY(-2px);
          background: #1a1a1a;
          box-shadow: 0 6px 12px rgba(0,0,0,0.3);
        }

        button.close-btn {
          background: #666;
        }

        button.close-btn:hover {
          background: #555;
        }

        .print-container {
          width: 210mm;
          margin: 0;
          padding: 12mm 12mm 18mm 12mm;
          background-color: white;
          page-break-after: always;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .header {
          text-align: center;
          margin-bottom: 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid #999;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
        }

        .header-left {
          flex: 1;
          text-align: left;
          font-size: 9pt;
          line-height: 1.6;
        }

        .header-left strong {
          color: #666;
          font-weight: 600;
          display: inline-block;
          min-width: 70px;
        }

        .logo-container {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-container img {
          max-width: 40px;
          max-height: 40px;
          object-fit: contain;
        }

        .header-right {
          flex: 1;
          text-align: left;
          font-size: 9pt;
          line-height: 1.6;
        }

        .header-right strong {
          color: #666;
          font-weight: 600;
          display: inline-block;
          min-width: 75px;
        }

        .hospital-name {
          font-size: 20pt;
          font-weight: 700;
          margin-bottom: 4px;
          color: #333;
          letter-spacing: 1px;
          text-align: center;
        }

        .report-title {
          text-align: center;
          font-size: 14pt;
          font-weight: 600;
          color: #555;
          margin: 8px 0 6px 0;
          padding: 4px 0;
          border-bottom: 1px solid #ccc;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .result-date {
          margin: 4px 0;
          font-weight: 600;
          font-size: 10pt;
          color: #555;
          padding: 4px 6px;
          background: #f5f5f5;
          border-left: 3px solid #999;
          border-radius: 4px;
        }

        .patient-info {
          display: flex;
          flex-wrap: wrap;
          gap: 8px 12px;
          margin: 4px 0 3px 0;
          padding: 4px 3px;
          background: #fafafa;
          border-left: 3px solid #999;
          border-radius: 4px;
          font-size: 9pt;
          line-height: 1.4;
          justify-content: flex-start;
          align-items: center;
        }

        .patient-info div {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .patient-info strong {
          color: #666;
          font-weight: 600;
          min-width: 65px;
        }

        .content {
          margin-top: 6px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 6px;
          page-break-inside: avoid;
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
          border-radius: 4px;
          overflow: hidden;
          font-size: 9.5pt;
        }

        th, td {
          padding: 10px;
          text-align: left;
        }

        th {
          background: #666;
          color: white;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          font-size: 9pt;
        }

        td {
          border-bottom: 1px solid #ccc;
          color: #555;
        }

        tbody tr:nth-child(even) {
          background-color: #f9f9f9;
        }

        tbody tr:hover {
          background-color: #f0f0f0;
        }

        .lab-section {
          margin-bottom: 6px;
          page-break-inside: avoid;
          padding-bottom: 2px;
        }

        .lab-title {
          text-align: center;
          font-size: 11pt;
          font-weight: 700;
          color: #333;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 4px;
          padding: 3px 0;
          border-bottom: 1px solid #999;
        }

        .histograms-block {
          margin-top: 10px;
          padding: 8px 12px;
          border: 1px solid #d4d8dd;
          border-radius: 4px;
          background: #fbfcfd;
          page-break-inside: avoid;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .histograms-title {
          font-size: 8.5pt;
          font-weight: 700;
          color: #2c3e50;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .histograms-grid {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: flex-start;
        }
        .histogram-card {
          flex: 1 1 200px;
          min-width: 180px;
          max-width: 260px;
          border: 1px solid #e0e4e8;
          border-radius: 3px;
          padding: 6px 8px 8px 8px;
          background: #ffffff;
          text-align: center;
          page-break-inside: avoid;
        }
        .histogram-name {
          font-size: 8pt;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 4px;
          letter-spacing: 0.3px;
        }
        .histogram-img {
          width: 100%;
          height: auto;
          max-height: 160px;
          display: block;
          margin: 0 auto;
          image-rendering: -webkit-optimize-contrast;
        }

        .narratives-block {
          margin-top: 8px;
          padding: 6px 10px;
          border: 1px solid #f1d59c;
          border-left: 4px solid #d4a017;
          border-radius: 4px;
          background: #fdf8ee;
          page-break-inside: avoid;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .narratives-title {
          font-size: 8pt;
          font-weight: 700;
          color: #7a5a10;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          margin-bottom: 3px;
        }
        .narratives-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px 14px;
        }
        .narrative-col {
          min-width: 0;
          border-left: 1px dashed #e5d3a3;
          padding-left: 8px;
        }
        .narrative-col:first-child {
          border-left: none;
          padding-left: 0;
        }
        .narrative-col-title {
          font-size: 8pt;
          font-weight: 800;
          color: #7a5a10;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          margin-bottom: 2px;
        }
        .narrative-list {
          margin: 0;
          padding-left: 14px;
          font-size: 8.5pt;
          color: #2c3e50;
          line-height: 1.45;
        }
        .narrative-list li {
          margin: 0;
          padding: 0;
        }

        .interpretation-block {
          margin-top: 10px;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 8.5pt;
          color: #333;
          background: #fafafa;
        }

        .interpretation-block p {
          margin: 0 0 6px 0;
          font-size: 8.5pt;
          line-height: 1.5;
        }

        .interpretation-block table {
          width: 100%;
          border-collapse: collapse;
          font-size: 8pt;
        }

        .interpretation-block table th {
          background: #2c3e50;
          color: #ffffff;
          font-weight: 700;
          border: 1px solid #1a252f;
          padding: 4px 6px;
          text-align: center;
        }

        .interpretation-block table th:first-child {
          text-align: left;
        }

        .interpretation-block table td {
          border: 1px solid #aaa;
          padding: 3px 6px;
          text-align: center;
        }

        .interpretation-block table td:first-child {
          text-align: left;
        }

        .footer {
          margin-top: 12px;
          padding-top: 6px;
          border-top: 1px solid #999;
          font-size: 9pt;
          color: #666;
          font-weight: 500;
        }

        .footer strong {
          color: #555;
        }

        @media print {
          /* Force background colors / images to print so the histogram
             cards and their borders survive the print step. Without this
             Chromium-based browsers drop the card backgrounds and the
             histograms appear floating on a white page. */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          body {
            padding: 0;
            background: white;
            margin: 0;
          }

          .screen-only {
            display: none !important;
          }

          .print-container {
            width: 210mm;
            height: auto;
            margin: 0;
            padding: 12mm 12mm 18mm 12mm;
            box-shadow: none;
            page-break-after: always;
          }

          table {
            page-break-inside: avoid;
            box-shadow: none;
          }

          .lab-section {
            page-break-inside: avoid;
          }

          .histograms-block,
          .histogram-card {
            page-break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
      <div class="screen-only">
        <div class="button-container">
          <button onclick="window.print()">Print / Save as PDF</button>
          <button class="close-btn" onclick="window.close()">Close</button>
        </div>
      </div>

      <div class="print-container">
        <!-- Hospital Header -->
        <div class="hospital-name">${company.name || 'Hospital Name'}</div>
        <div class="header">
          <div class="header-left">
            <strong>Address:</strong> ${company.digital_add || 'N/A'}<br>
            <strong>Location:</strong> ${company.location || 'N/A'}<br>
            <strong>Mission:</strong> ${company.mmda || 'Quality Healthcare'}
          </div>
         <div class="logo-container">
            ${company.logo 
              ? `<img src="${company.logo}" alt="Hospital Logo" style="max-width:80px; max-height:80px; object-fit:contain;">`
              : ''
            }
          </div>
          <div class="header-right">
            <strong>Telephone:</strong> ${company.phone || 'N/A'}<br>
            <strong>Email:</strong> ${company.email || 'N/A'}<br>
            <strong>Website:</strong> ${company.website || 'N/A'}
          </div>
        </div>

        <!-- Result Date -->
        <div class="result-date">
          <strong>Date Of Results:</strong> ${dateOfResult}
        </div>

        <!-- Patient Information -->
        <div class="patient-info">
          <div>
            <strong>Patient ID:</strong> ${selectedPatient?.id || 'N/A'}
          </div>
          <div>
            <strong>Name:</strong> ${selectedPatient?.fullName || 'N/A'}
          </div>
          <div>
            <strong>Gender:</strong> ${selectedPatient?.gender || 'N/A'}
          </div>
          <div>
            <strong>Age:</strong> ${selectedPatient?.age || 'N/A'}
          </div>
          <div>
            <strong>Tel #:</strong> ${selectedPatient?.tel || 'N/A'}
          </div>
        </div>

        <!-- Report Title -->
        <div class="report-title">
          LAB RESULTS
        </div>

        <!-- Content -->
        <div class="content">
          ${labsHTML}
        </div>

        <!-- Footer -->
        <div class="footer">
          <strong>Printed by:</strong> ${userInfo?.user_name || userInfo?.name || 'Not specified'}
        </div>
      </div>
    </body>
    </html>
  `;

  return html;
};
